// ─────────────────────────────────────────────────────────────────
// Glass-i — Proforma Grid Columns settings page (Stage 6Q.4).
//
// Tenant-wide control over which optional Proforma grid column groups
// are visible. Configured once, shared by all users, applied to every
// proforma (new + existing). Essential columns (#, Type, Item Desc,
// Qty, Total) are always shown; Area / Location / Fabrication stay on
// the editor's own controls panel and are intentionally not here.
// ─────────────────────────────────────────────────────────────────

import { useEffect, useState, useCallback } from 'react';
import {
  Card, Switch, Button, Space, Alert, Spin, Divider, Tag, message
} from 'antd';
import { SaveOutlined, ReloadOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { proformaGridSettingsApi } from './proformaGridSettingsApi';
import { PROFORMA_TOGGLEABLE_COLUMNS } from '../../sales/proforma/proformaHelpers';

export default function ProformaGridSettingsPage() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [bootError, setBootError] = useState(null);
  // Per-column visibility map: { [key]: boolean (visible) }.
  const [visible, setVisible] = useState(() => {
    const m = {};
    for (const c of PROFORMA_TOGGLEABLE_COLUMNS) m[c.key] = true;
    return m;
  });

  const hydrate = useCallback((hiddenColumns) => {
    const hidden = new Set(Array.isArray(hiddenColumns) ? hiddenColumns : []);
    const m = {};
    for (const c of PROFORMA_TOGGLEABLE_COLUMNS) m[c.key] = !hidden.has(c.key);
    setVisible(m);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setBootError(null);
    try {
      const s = await proformaGridSettingsApi.get();
      hydrate(s && s.hiddenColumns);
    } catch (e) {
      setBootError(e?.response?.data?.error?.message || e.message || 'Failed to load settings.');
    } finally {
      setLoading(false);
    }
  }, [hydrate]);

  useEffect(() => { load(); }, [load]);

  const toggle = (key) => setVisible((m) => ({ ...m, [key]: !m[key] }));

  const onSave = async () => {
    const hiddenColumns = PROFORMA_TOGGLEABLE_COLUMNS
      .filter((c) => visible[c.key] === false)
      .map((c) => c.key);
    setSaving(true);
    try {
      const saved = await proformaGridSettingsApi.update({ hiddenColumns });
      hydrate(saved && saved.hiddenColumns);
      message.success('Grid column visibility saved.');
    } catch (e) {
      message.error(e?.response?.data?.error?.message || e.message || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const allOn  = () => setVisible(() => {
    const m = {}; for (const c of PROFORMA_TOGGLEABLE_COLUMNS) m[c.key] = true; return m;
  });
  const hiddenCount = PROFORMA_TOGGLEABLE_COLUMNS.filter((c) => visible[c.key] === false).length;

  return (
    <div style={{ padding: 16, maxWidth: 680 }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 22, fontWeight: 600, color: '#0f172a' }}>
          Proforma Grid Columns
        </div>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
          Choose which optional columns appear in the Proforma editor grid — tenant-wide, shared by all users.
        </div>
      </div>

      {bootError ? (
        <Alert type="error" message="Failed to load" description={bootError}
          style={{ marginBottom: 12 }} showIcon />
      ) : null}

      <Spin spinning={loading}>
        <Card size="small" styles={{ body: { padding: 8 } }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '6px 10px' }}>
            <span style={{ fontSize: 12, color: '#64748b' }}>
              {hiddenCount === 0 ? 'All optional columns visible' : `${hiddenCount} column(s) hidden`}
            </span>
            <Button size="small" type="link" onClick={allOn} disabled={hiddenCount === 0}>
              Show all
            </Button>
          </div>
          <Divider style={{ margin: '4px 0' }} />
          {PROFORMA_TOGGLEABLE_COLUMNS.map((c) => (
            <div key={c.key} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '7px 10px', borderBottom: '1px solid #f1f5f9'
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                {visible[c.key]
                  ? <EyeOutlined style={{ color: '#16a34a' }} />
                  : <EyeInvisibleOutlined style={{ color: '#94a3b8' }} />}
                <span style={{ fontSize: 13, color: '#0f172a' }}>{c.label}</span>
                {!visible[c.key] ? <Tag color="default" style={{ margin: 0 }}>Hidden</Tag> : null}
              </span>
              <Switch size="small" checked={visible[c.key] !== false}
                onChange={() => toggle(c.key)} />
            </div>
          ))}
          <div style={{ padding: '10px', display: 'flex', justifyContent: 'space-between',
            alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>
              # · Type · Item Desc · Qty · Total are always shown. Area / Location / Fabrication stay on the editor's controls.
            </span>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={load} disabled={saving}>Reset</Button>
              <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={onSave}>
                Save
              </Button>
            </Space>
          </div>
        </Card>
      </Spin>
    </div>
  );
}
